import { interpolate } from "remotion";
import { ZoomKeyframe, ZoomConfig } from "../../types";

/**
 * Represents the calculated zoom state at a specific frame
 */
export interface ZoomState {
  scale: number;      // Current scale factor (1 = no zoom)
  originX: number;    // Transform origin X as percentage (0-100)
  originY: number;    // Transform origin Y as percentage (0-100)
  isActive: boolean;  // Whether we're within a zoom keyframe
}

/**
 * Default zoom state when no zoom is active
 */
const DEFAULT_ZOOM_STATE: ZoomState = {
  scale: 1,
  originX: 50,
  originY: 50,
  isActive: false,
};

/**
 * Calculates the current zoom state based on frame position and keyframes.
 * Uses Remotion's interpolate for smooth transitions between states.
 *
 * @param frame - Current frame relative to overlay start (0 = first frame of overlay)
 * @param zoomConfig - The overlay's zoom configuration
 * @param transitionFrames - Number of frames for entry/exit transitions (default: 15)
 * @returns ZoomState with current scale and transform origin
 */
export function calculateZoomState(
  frame: number,
  zoomConfig: ZoomConfig | undefined,
  transitionFrames: number = 15
): ZoomState {
  // Return default state if zoom is disabled or no keyframes
  if (!zoomConfig?.enabled || !zoomConfig.keyframes?.length) {
    return DEFAULT_ZOOM_STATE;
  }

  const { keyframes } = zoomConfig;

  // Sort keyframes by start frame
  const sortedKeyframes = [...keyframes].sort((a, b) => a.startFrame - b.startFrame);

  // Find the active keyframe (if frame is within any keyframe's range)
  const activeKeyframe = sortedKeyframes.find(
    kf => frame >= kf.startFrame && frame <= kf.endFrame
  );

  // If no active keyframe, return default state
  if (!activeKeyframe) {
    return DEFAULT_ZOOM_STATE;
  }

  // Find previous keyframe for transition reference
  const prevKeyframe = sortedKeyframes
    .filter(kf => kf.endFrame < activeKeyframe.startFrame)
    .sort((a, b) => b.endFrame - a.endFrame)[0];

  // Find next keyframe for exit transition reference
  const nextKeyframe = sortedKeyframes
    .filter(kf => kf.startFrame > activeKeyframe.endFrame)
    .sort((a, b) => a.startFrame - b.startFrame)[0];

  // Calculate transition boundaries
  const entryEnd = activeKeyframe.startFrame + transitionFrames;
  const exitStart = activeKeyframe.endFrame - transitionFrames;

  // Prevent transitions from overlapping in very short keyframes
  const keyframeDuration = activeKeyframe.endFrame - activeKeyframe.startFrame;
  const effectiveTransitionFrames = Math.min(transitionFrames, keyframeDuration / 3);
  const safeEntryEnd = activeKeyframe.startFrame + effectiveTransitionFrames;
  const safeExitStart = activeKeyframe.endFrame - effectiveTransitionFrames;

  // Get animation style (default to 'both')
  const animationStyle = activeKeyframe.animationStyle ?? 'both';
  const hasEntryAnimation = animationStyle === 'both' || animationStyle === 'entry-only';
  const hasExitAnimation = animationStyle === 'both' || animationStyle === 'exit-only';

  // Case 1: Entry transition phase
  if (frame < safeEntryEnd && hasEntryAnimation) {
    const fromScale = prevKeyframe?.scale ?? 1;
    const fromX = prevKeyframe?.xPct ?? 50;
    const fromY = prevKeyframe?.yPct ?? 50;

    return {
      scale: interpolate(
        frame,
        [activeKeyframe.startFrame, safeEntryEnd],
        [fromScale, activeKeyframe.scale],
        { extrapolateRight: "clamp", extrapolateLeft: "clamp" }
      ),
      originX: interpolate(
        frame,
        [activeKeyframe.startFrame, safeEntryEnd],
        [fromX, activeKeyframe.xPct],
        { extrapolateRight: "clamp", extrapolateLeft: "clamp" }
      ),
      originY: interpolate(
        frame,
        [activeKeyframe.startFrame, safeEntryEnd],
        [fromY, activeKeyframe.yPct],
        { extrapolateRight: "clamp", extrapolateLeft: "clamp" }
      ),
      isActive: true,
    };
  }

  // Case 2: Exit transition phase
  if (frame >= safeExitStart && hasExitAnimation) {
    const toScale = nextKeyframe?.scale ?? 1;
    const toX = nextKeyframe?.xPct ?? 50;
    const toY = nextKeyframe?.yPct ?? 50;

    return {
      scale: interpolate(
        frame,
        [safeExitStart, activeKeyframe.endFrame],
        [activeKeyframe.scale, toScale],
        { extrapolateRight: "clamp", extrapolateLeft: "clamp" }
      ),
      originX: interpolate(
        frame,
        [safeExitStart, activeKeyframe.endFrame],
        [activeKeyframe.xPct, toX],
        { extrapolateRight: "clamp", extrapolateLeft: "clamp" }
      ),
      originY: interpolate(
        frame,
        [safeExitStart, activeKeyframe.endFrame],
        [activeKeyframe.yPct, toY],
        { extrapolateRight: "clamp", extrapolateLeft: "clamp" }
      ),
      isActive: true,
    };
  }

  // Case 3: Fully within keyframe (no transition)
  return {
    scale: activeKeyframe.scale,
    originX: activeKeyframe.xPct,
    originY: activeKeyframe.yPct,
    isActive: true,
  };
}

/**
 * Generates CSS transform and transformOrigin styles for zoom effect
 *
 * @param zoomState - The calculated zoom state
 * @returns React.CSSProperties object with transform styles
 */
export function getZoomTransformStyle(zoomState: ZoomState): React.CSSProperties {
  if (zoomState.scale === 1 && !zoomState.isActive) {
    return {};
  }

  return {
    transform: `scale(${zoomState.scale})`,
    transformOrigin: `${zoomState.originX}% ${zoomState.originY}%`,
  };
}

/**
 * Validates that keyframes don't overlap and are properly configured
 *
 * @param keyframes - Array of zoom keyframes to validate
 * @returns Object with valid boolean and optional error message
 */
export function validateKeyframes(keyframes: ZoomKeyframe[]): {
  valid: boolean;
  error?: string;
} {
  if (!keyframes || keyframes.length === 0) {
    return { valid: true };
  }

  const sorted = [...keyframes].sort((a, b) => a.startFrame - b.startFrame);

  // Check for overlapping keyframes
  for (let i = 0; i < sorted.length - 1; i++) {
    if (sorted[i].endFrame >= sorted[i + 1].startFrame) {
      return {
        valid: false,
        error: `Keyframes overlap: one ends at frame ${sorted[i].endFrame} but next starts at frame ${sorted[i + 1].startFrame}`,
      };
    }
  }

  // Validate individual keyframes
  for (const kf of keyframes) {
    if (kf.startFrame >= kf.endFrame) {
      return {
        valid: false,
        error: `Invalid keyframe: start frame (${kf.startFrame}) must be less than end frame (${kf.endFrame})`,
      };
    }
    if (kf.scale < 1 || kf.scale > 4) {
      return {
        valid: false,
        error: `Invalid scale ${kf.scale}: must be between 1 and 4`,
      };
    }
    if (kf.xPct < 0 || kf.xPct > 100 || kf.yPct < 0 || kf.yPct > 100) {
      return {
        valid: false,
        error: `Invalid position: x and y must be between 0 and 100`,
      };
    }
  }

  return { valid: true };
}

/**
 * Creates a new zoom keyframe with default values
 *
 * @param startFrame - Start frame of the keyframe
 * @param endFrame - End frame of the keyframe
 * @param scale - Zoom scale (default: 1.5)
 * @param xPct - X position percentage (default: 50)
 * @param yPct - Y position percentage (default: 50)
 * @returns A new ZoomKeyframe object
 */
export function createZoomKeyframe(
  startFrame: number,
  endFrame: number,
  scale: number = 1.5,
  xPct: number = 50,
  yPct: number = 50
): ZoomKeyframe {
  return {
    id: `zoom-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    startFrame,
    endFrame,
    scale,
    xPct,
    yPct,
  };
}

/**
 * Clamps keyframes to fit within overlay bounds
 *
 * @param keyframes - Array of keyframes to clamp
 * @param maxFrame - Maximum frame (overlay durationInFrames)
 * @returns Array of clamped keyframes (removes invalid ones)
 */
export function clampKeyframesToBounds(
  keyframes: ZoomKeyframe[],
  maxFrame: number
): ZoomKeyframe[] {
  return keyframes
    .map(kf => ({
      ...kf,
      startFrame: Math.max(0, Math.min(kf.startFrame, maxFrame - 1)),
      endFrame: Math.max(1, Math.min(kf.endFrame, maxFrame)),
    }))
    .filter(kf => kf.startFrame < kf.endFrame); // Remove invalid keyframes
}
